import { giphyBlack } from '@giphy/js-brand'
import React, { SyntheticEvent, useContext, useEffect, useState } from 'react'
import MessageContext from 'shared/contexts/message'
import DotsLoader from 'shared/components/loaders/dots'
import styled from 'styled-components'
import { FONT_FILE_EXTENSIONS } from '../../../constants'
import CreationToolSuperCutField from '../../../types/super-cut-field'
import { preloadFont } from '../../../utils/preload'
import { FileButton } from '../../buttons'
import SelectList from '../../select-list'
import { CreationCaptureInfoContext } from '../../supercut/shared/components/capture-info/manager'
import API from '../../supercut/shared/utils/api'
import FieldTitle from './field-title'

const api = new API()

const Container = styled.div`
    grid-column: span 1;
    min-width: 0;
    position: relative;
`

const UploadButton = styled(FileButton)`
    width: 100%;
    text-align: left;
`

type Props = {
    field: CreationToolSuperCutField
}

const CreationCaptureFontField = ({ field }: Props) => {
    const { font, setFont, previousFonts, setPreviousFonts } = useContext(CreationCaptureInfoContext)
    const { sendMessage } = useContext(MessageContext)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (font?.font && font?.id) {
            try {
                preloadFont(font?.font, 'uploadedFont')
            } catch (e) {
                sendMessage('An error occurred while trying to load this font', 'error')
            }
        }
    }, [font, setPreviousFonts, sendMessage])

    const onChange = (val: string) => {
        if (font?.id === val) {
            setFont()
        } else {
            const selected = previousFonts.find(font => font.id === val)
            setFont(selected)
        }
    }

    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget
        if (files && files?.length > 0) {
            // TODO: Font file type and size validation.
            setIsLoading(true)
            const font = await api.uploadCaptureAsset(files[0], 'ttf')
            const fontTitle = files[0].name
                .replace(/-/g, ' ')
                .replace(/_/g, ' ')
                .split('.')[0]
            const newFont = { font: font, title: fontTitle, id: 'uploadedFont' }

            try {
                await preloadFont(font, newFont.id)
                setPreviousFonts([newFont])
                setFont(newFont)
            } catch (e) {
                sendMessage('An error occurred while trying to load this font', 'error')
            }

            setIsLoading(false)
        }
    }

    return (
        <Container>
            {isLoading ? (
                <DotsLoader isAbsolute={true} />
            ) : (
                <>
                    <FieldTitle field={field} />
                    <SelectList
                        {...{
                            onChange,
                            popUpColor: giphyBlack,
                            value: font?.id,
                            options: previousFonts.map(f => ({
                                value: f.id,
                                label: <span style={{ fontFamily: 'uploadedFont' }}>{f.title}</span>,
                            })),
                        }}
                    >
                        <UploadButton>
                            Upload New
                            <input type="file" accept={FONT_FILE_EXTENSIONS.join(',')} onInput={onFileInput} />
                        </UploadButton>
                    </SelectList>
                </>
            )}
        </Container>
    )
}

export default CreationCaptureFontField
